import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import moment from "moment";
import RevenueContainer from "../../../state/containers/RevenueContainer";
import { OrganizationConfig } from "../../../models/revenue/BillingConfig";
import IconTooltip from "../../../components/general/IconTooltip";
import {
    buildVariableRevenueExport, EXPORT_COLUMN, isDataLoadedFor, isVariableExportContract, serializeVariableRevenueExport,
} from "../../../utils/revenueExport";
import {
    color_colors_growth,
    color_colors_ocean,
    color_defaults_white,
    color_shades_light,
    color_text_light,
} from "../../../constants/colors";

const infoIcon = require("../../../content/images/info_icon.svg");

const EXPORT_HELP = "Columns match the 2026 Fixed-Variable Revenue tab (Type through $), then Retail Health's "
    + "inbound, outbound and combined minutes and outbound rate, Month and Application ID. $ = Bill × rate "
    + "(variable amount only; the fixed fee is its own column). "
    + "Bill: Answering = calls over the included amount; Ordering = completed orders; Retail Health = inbound + "
    + "outbound AI minutes, each at its own rate. Only Paying organizations and apps are included, and usage "
    + "before an app's launch date is excluded. Copy for Excel leaves out the header row so it can be pasted "
    + "below the last row of the table; Export CSV includes it.";

// Names a few items so they can be acted on; summarizes larger counts.
const listOrCount = (names: string[], listLabel: string, countLabel: string): string =>
    !names.length ? ""
        : names.length < 5 ? ` ${listLabel}: ${names.slice().sort((a, b) => a.localeCompare(b)).join(", ")}.`
        : ` ${names.length} ${countLabel}.`;

interface RevenueExportActionsProps {
    revenueContainer: RevenueContainer;
}

const RevenueExportActions: React.FC<RevenueExportActionsProps> = ({ revenueContainer }) => {
    const [message, setMessage] = useState("");
    const [copyText, setCopyText] = useState("");
    const [toast, setToast] = useState("");

    useEffect(() => {
        if (!toast) return;
        const timer = window.setTimeout(() => setToast(""), 3000);
        return () => window.clearTimeout(timer);
    }, [toast]);
    const state = revenueContainer.state;
    const month = moment(state.selectedMonth).format("YYYY-MM");
    const isLoading = state.isLoadingApplications || state.isLoadingEngagements;
    // Only export data from a completed load of the selected month and settings.
    const disabledReason = state.revenueConfig.organizations.length === 0 ? "Add organizations to export."
        : isLoading || state.isRecalculating ? "Available once loading finishes."
        : !isDataLoadedFor(state.loadedData, month, state.revenueConfig.organizations) ? "Click Load Data to export this month."
        : "";

    const exportData = async (copy: boolean) => {
        if (disabledReason) return;
        setMessage("");
        setCopyText("");
        try {
            // Only Paying orgs are billed. Orgs that failed to load (e.g. no access) are left out
            // rather than exported with partial usage.
            const variableOrgs = state.revenueConfig.organizations.filter(org => isVariableExportContract(org.contractType));
            const payingOrgs = variableOrgs.filter(org => org.paying === true);
            const exportOrgs = payingOrgs.filter(org => !state.orgLoadErrors[org.organizationId]);
            const orgName = (org: OrganizationConfig) =>
                state.organizationNamesById[org.organizationId] || org.organizationName || org.organizationId;
            const notPayingNames = variableOrgs.filter(org => org.paying !== true).map(orgName);
            const notLoadedNames = payingOrgs.filter(org => state.orgLoadErrors[org.organizationId]).map(orgName);
            const rows = buildVariableRevenueExport(month, exportOrgs, state.revenueByOrg,
                state.applicationLayoutsByOrg, state.organizationNamesById, state.applicationsByOrg);
            if (!rows.length) throw new Error(payingOrgs.length
                ? "No loaded Paying AI Answering, Ordering or Retail Health apps to export."
                : "No Paying AI Answering, Ordering or Retail Health organizations to export.");
            const missingLaunchDates = rows.filter(row => row[EXPORT_COLUMN.launchDate] === "")
                .map(row => String(row[EXPORT_COLUMN.assistantName]));
            const summary = `${rows.length} ${rows.length === 1 ? "row" : "rows"} for ${moment(state.selectedMonth).format("MMMM")}.`
                + listOrCount(notLoadedNames, "Not loaded and excluded", "orgs not loaded and excluded")
                + listOrCount(notPayingNames, "Not Paying, excluded", "Not Paying orgs excluded")
                + listOrCount(missingLaunchDates, "Missing launch date", "missing launch dates");
            if (copy) {
                const text = serializeVariableRevenueExport(rows, "\t", false);
                try {
                    await navigator.clipboard.writeText(text);
                    setMessage(`Copied ${summary}`);
                    setToast("Copied — paste below the last row in Excel");
                } catch {
                    setCopyText(text);
                    setMessage("Clipboard blocked. Copy the text below.");
                }
            } else {
                const blob = new Blob(["﻿", serializeVariableRevenueExport(rows, ",")], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `voicify-variable-revenue-${month}.csv`;
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                window.setTimeout(() => URL.revokeObjectURL(url), 1000);
                setMessage(`Exported ${summary}`);
            }
        } catch (error) {
            setMessage(error.message || "Export failed.");
        }
    };

    return <ExportGroup>
        {message && <ExportMessage role="status">{message}</ExportMessage>}
        <span title={disabledReason}>
            <ExportButton disabled={!!disabledReason} onClick={() => exportData(true)}>Copy for Excel</ExportButton>
        </span>
        <span title={disabledReason}>
            <ExportButton disabled={!!disabledReason} onClick={() => exportData(false)}>Export CSV</ExportButton>
        </span>
        <IconTooltip text={EXPORT_HELP} icon={infoIcon} place="bottomRight" />
        {toast && <Toast role="status">{toast}</Toast>}
        {copyText && <CopyFallback>
            <textarea aria-label="Variable billing to copy" readOnly value={copyText} onFocus={event => event.target.select()} />
            <button onClick={() => setCopyText("")}>Close</button>
        </CopyFallback>}
    </ExportGroup>;
};

const Toast = styled.div`
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1000;
    padding: 10px 16px;
    border-radius: 6px;
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(48, 69, 98, 0.2);
`;

const ExportGroup = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 12px;
    border-left: 1px solid ${color_shades_light};
`;

const ExportButton = styled.button`
    background: ${color_defaults_white};
    color: ${color_colors_ocean};
    border: 1px solid ${color_colors_ocean};
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        background: ${color_colors_ocean};
        color: ${color_defaults_white};
    }

    &:disabled {
        color: ${color_text_light};
        border-color: ${color_shades_light};
        cursor: not-allowed;
        pointer-events: none;
    }
`;

const ExportMessage = styled.span`
    font-size: 12px;
    color: ${color_text_light};
    white-space: nowrap;
`;

const CopyFallback = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 20;
    width: 420px;
    padding: 8px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(48, 69, 98, 0.15);
    textarea { width: 100%; height: 120px; }
    button { margin-top: 4px; cursor: pointer; }
`;

export default RevenueExportActions;
